import * as React from "react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";
import { convertUtcToLocal } from "../../helpers/CommonHelpers";
import { useOrganization } from "../../contexts/OrganizationContext";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";
import { ShadowFolderFinding } from "../../models/RenamedFolderFinding";
import RenamedFoldersRepository from "../../repositories/RenamedFoldersRepository";
import { FilterCombobox } from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import TableEmptyState from "../common/TableEmptyState";
import TablePagination from "../common/Table/TablePagination";
import { SearchInput } from "../common/Table/SearchInput";
import { useTableHandlers } from "../common/Table/Hooks/UseTableHandlers";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  filterSearchClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import MigrationEntityCell from "./MigrationEntityCell";

const entityTypeOptions = [
  MigrationType.CasesMigration,
  MigrationType.IntakesMigration,
  MigrationType.ProvidersMigration,
].map((type) => ({ value: type, label: MigrationTypeLabels[type] }));

const attributionOptions = [
  { value: "attributed", label: "Linked to an entity" },
  { value: "unattributed", label: "Unattributed" },
];

export const UntrackedFoldersTable: React.FC = () => {
  const repository = new RenamedFoldersRepository();
  const { selectedOrganizationId } = useOrganization();

  const [entityTypeFilter, setEntityTypeFilter] = useState<MigrationType | null>(null);
  const [attributionFilter, setAttributionFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [findings, setFindings] = useState<ShadowFolderFinding[]>([]);

  const { order, page, rowsPerPage, rows, changeOrder, handleRowsPerPageChange, setPage, setRows } =
    useTableHandlers({ order: "desc", sort: "lastReportedOn", page: 0, rowsPerPage: 50 });

  const attributed = attributionFilter === null ? null : attributionFilter === "attributed";

  const findingsQuery = useQuery({
    queryKey: [
      QUERY_KEYS.SHADOW_FOLDER_FINDINGS,
      selectedOrganizationId,
      entityTypeFilter,
      attributionFilter,
      searchQuery,
      page,
      rowsPerPage,
      order,
    ],
    queryFn: () =>
      repository.getShadowFolderFindings({
        entityType: entityTypeFilter,
        attributed,
        search: searchQuery || null,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        descending: order === "desc",
      }),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
    enabled: !!selectedOrganizationId,
  });

  useEffect(() => {
    if (findingsQuery.data) {
      setFindings(findingsQuery.data.data);
      setRows(findingsQuery.data.paging?.totalCount ?? findingsQuery.data.data.length);
    }
  }, [findingsQuery.data, setRows]);

  const loading = findingsQuery.isFetching || findingsQuery.isError;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={fileFiltersBoxClassName}>
        <div className={filterSearchClassName}>
          <SearchInput
            label="Search"
            placeholder="Folder or drive name"
            onSearch={(value) => {
              setSearchQuery(value);
              setPage(0);
            }}
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={entityTypeFilter}
            onChange={(value) => {
              setEntityTypeFilter(value as MigrationType | null);
              setPage(0);
            }}
            options={entityTypeOptions}
            placeholder="All types"
          />
        </div>
        <div className={filterComboboxClassName}>
          <FilterCombobox
            value={attributionFilter}
            onChange={(value) => {
              setAttributionFilter(value);
              setPage(0);
            }}
            options={attributionOptions}
            placeholder="All folders"
          />
        </div>
      </div>

      <div className={cn(tableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[28%]">Folder</TableHead>
              <TableHead className="w-[16%]">Drive</TableHead>
              <TableHead className="w-[26%]">Stranded by</TableHead>
              <TableHead className="w-[14%]">Documents</TableHead>
              <TableHead className="w-[16%]">
                <SortableTableColumnHeader
                  label="Last seen"
                  sortProperty="lastReportedOn"
                  getSort={() => "lastReportedOn"}
                  setSort={() => undefined}
                  getOrder={() => order}
                  setOrder={(next) => changeOrder(next)}
                />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {findings.map((finding) => (
              <TableRow key={finding.id}>
                <TableCell>
                  {finding.webUrl ? (
                    <a
                      href={finding.webUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <span className="truncate">{finding.name}</span>
                      <ExternalLink className="size-3 shrink-0" />
                    </a>
                  ) : (
                    <span className="truncate">{finding.name}</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{finding.driveName}</TableCell>
                <TableCell>
                  <MigrationEntityCell
                    entityType={finding.entityType}
                    migrationEntityId={finding.migrationEntityId}
                    entityLabel={finding.entityLabel}
                  />
                </TableCell>
                {/* Overwritten on every sweep, so it describes the folder now and never a total across sweeps. */}
                <TableCell title="Documents counted when the folder was last seen">
                  {finding.documentCount}
                </TableCell>
                <TableCell>{convertUtcToLocal(finding.lastReportedOn)}</TableCell>
              </TableRow>
            ))}
            {findings.length === 0 && !loading && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={99}>
                  <TableEmptyState
                    title="No untracked folders recorded"
                    description="Folders that NFS holds no id for appear here once a client has swept this organization."
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
    </div>
  );
};

export default UntrackedFoldersTable;
